/**
 * The web app's side of the Android app (see native.ts for the message channel):
 *
 * - calls: tell the app when a call starts, the camera toggles, the mic restarts or the call
 *   ends; "Hang up" in its notification leaves the call;
 * - phone notifications: register the app's push token with the server, again on every start;
 * - in the background: tell the server, which then pushes to this phone (calls ring natively)
 *   while the connection stays up, so the user still shows as online;
 * - "Accept" on an incoming-call notification: open the conversation and join the call;
 * - progress of an app update (lib/appUpdate.ts).
 */
import type { NativePushInfo } from '@comms/shared';
import { displayName, useStore } from '../store';
import { api } from './api';
import { onUpdateState } from './appUpdate';
import {
  nativeApp,
  nativeCall,
  setAppInBackground,
  useNativeAudio,
  type FcmAndroidConfig,
} from './native';
import { getGateway } from './session';
import { joinCall, leaveVoice, restartMicrophone, restartPlayback, useVoice } from './voice';

const PUSH_PREF = 'push.android';
const TOKEN_PREF = 'push.android.token';

const pref = (k: string) => {
  try {
    return localStorage.getItem(k);
  } catch {
    return null;
  }
};
const setPref = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k);
    else localStorage.setItem(k, v);
  } catch {
    /* ignore */
  }
};

export class PushNotSetUpError extends Error {
  constructor() {
    super("This server isn't set up for phone notifications yet.");
  }
}

/** The server's Firebase ids for the Android app, or null if it doesn't send phone pushes. */
async function serverPushConfig(): Promise<FcmAndroidConfig | null> {
  const info = await api<NativePushInfo>('GET', '/push/native');
  const a = info.fcm?.android;
  return info.fcm && a ? { projectId: info.fcm.projectId, ...a } : null;
}

export const nativePushOn = () => pref(PUSH_PREF) === 'on';

/** Turn phone notifications on (or refresh the registration): app token → server. */
export async function enableNativePush() {
  const config = await serverPushConfig();
  if (!config) throw new PushNotSetUpError();
  const { token, keys } = await nativeApp.enablePush(config);
  await api('POST', '/push/devices', { platform: 'fcm', token, keys });
  setPref(PUSH_PREF, 'on');
  setPref(TOKEN_PREF, token);
}

export async function disableNativePush() {
  const token = pref(TOKEN_PREF);
  setPref(PUSH_PREF, null);
  setPref(TOKEN_PREF, null);
  nativeApp.disablePush();
  if (token) await api('DELETE', '/push/devices', { token }).catch(() => {});
}

function callTitle(): string {
  const { kind, channelId } = useVoice.getState();
  const s = useStore.getState();
  const c = channelId ? s.channels[channelId] : undefined;
  if (!c) return '';
  if (kind === 'guild' || c.name) return c.name ?? '';
  const others = (c.recipients ?? []).filter((r) => r.id !== s.me?.id);
  return others.map((r) => displayName(s, r.id)).join(', ');
}

/** Go to an app path without reloading (React Router follows history changes). */
function navigate(url: string) {
  if (!url.startsWith('/') || url.startsWith('//')) return;
  window.history.pushState({}, '', url);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

/** Once the live connection is up (DM list, call state), run fn. */
function whenReady(fn: () => void) {
  if (useStore.getState().connection === 'ready') return fn();
  const off = useStore.subscribe((s) => {
    if (s.connection !== 'ready') return;
    off();
    fn();
  });
}

/** How long the call may play as media audio before playing it again. */
const PLAYBACK_GRACE_MS = 1500;
let playbackCheck: number | undefined;

/**
 * The app reports whether the call plays as phone-call audio. If it's media (loudspeaker
 * only) for a moment, play it again from scratch; after a couple of tries, say so.
 */
function onPlayback(voice: boolean) {
  window.clearTimeout(playbackCheck);
  if (voice) {
    useNativeAudio.setState({ mediaOnly: false });
    return;
  }
  playbackCheck = window.setTimeout(() => {
    if (useVoice.getState().status !== 'connected') return;
    if (!restartPlayback()) useNativeAudio.setState({ mediaOnly: true });
  }, PLAYBACK_GRACE_MS);
}

function onAppState(foreground: boolean) {
  setAppInBackground(!foreground);
  getGateway()?.setBackground(!foreground);
}

/**
 * The home-screen web app on an iPhone: iOS suspends it a few seconds after it leaves the
 * screen. Tell the server straight away, so those seconds don't hold back a push (the page
 * can't show one there), and reconnect as soon as it's back.
 */
export function startIosWebApp() {
  if ((navigator as Navigator & { standalone?: boolean }).standalone !== true) return;
  document.addEventListener('visibilitychange', () => onAppState(!document.hidden));
}

export function startNativeApp() {
  if (!nativeCall.available()) return;
  nativeApp.hello({
    appState: onAppState,
    open: navigate,
    updateState: onUpdateState,
    playback: onPlayback,
    acceptCall: (channelId) => {
      navigate(`/channels/@me/${channelId}`);
      whenReady(() => void joinCall(channelId));
    },
  });
  nativeCall.onHangup(() => void leaveVoice());

  // Refresh the push registration each time someone signs in (tokens can change).
  let registeredFor: string | null = null;
  useStore.subscribe((s) => {
    const me = s.me?.id ?? null;
    if (!me || me === registeredFor || !nativePushOn()) return;
    registeredFor = me;
    void enableNativePush().catch((err: unknown) => console.warn('phone notifications', err));
  });

  // The call moved to another output (speaker, earbuds…): phones can leave the open
  // microphone silent when that happens, so open it again.
  useNativeAudio.subscribe((now, before) => {
    if (!before.current || !now.current || now.current === before.current) return;
    const { status } = useVoice.getState();
    if (status === 'connected' || status === 'reconnecting')
      restartMicrophone('audio output changed');
  });

  useVoice.subscribe((now, before) => {
    const live = now.status === 'connected' || now.status === 'reconnecting';
    const wasLive = before.status === 'connected' || before.status === 'reconnecting';
    if (live && !wasLive) nativeCall.start(now.camera, callTitle());
    else if (!live && wasLive && now.status === 'idle') nativeCall.end();
    else if (live && now.camera !== before.camera) nativeCall.update(now.camera, callTitle());
    else if (live && before.muted && !now.muted) nativeCall.reapply();
  });
}
